import type { EnemyArchetype } from './types';

export type EnemyAttackPattern = 'lunge' | 'slam' | 'shot';
export type EnemyRank = 'common' | 'dominant';

export type EnemyBehaviorState =
  | { kind: 'idle' }
  | { kind: 'chase' }
  | { kind: 'reposition' }
  | { kind: 'telegraph'; attack: EnemyAttackPattern; startedAt: number; until: number }
  | { kind: 'recover'; until: number }
  | { kind: 'dead' };

export type EnemyIntent =
  | { kind: 'none' }
  | { kind: 'move'; dx: number; dy: number; speedMultiplier: number }
  | { kind: 'show-telegraph'; attack: EnemyAttackPattern; until: number }
  | { kind: 'lunge'; dx: number; dy: number }
  | { kind: 'slam'; radius: number }
  | { kind: 'shoot'; dx: number; dy: number };

export interface EnemyBehaviorInput {
  archetype: EnemyArchetype;
  rank: EnemyRank;
  state: EnemyBehaviorState;
  nowMs: number;
  distance: number;
  direction: { x: number; y: number };
  hp: number;
  maxHp: number;
  telegraphMs: number;
  recoveryMs: number;
  attackRange: number;
  preferredRange: number;
}

export interface EnemyBehaviorResult {
  state: EnemyBehaviorState;
  intent: EnemyIntent;
}

const attackFor = (archetype: EnemyArchetype): EnemyAttackPattern =>
  archetype === 'fast' ? 'lunge' : archetype === 'tank' ? 'slam' : 'shot';

const timingMultiplier = (input: EnemyBehaviorInput): number =>
  input.rank === 'dominant' && input.hp / Math.max(1, input.maxHp) < 0.5 ? 0.8 : 1;

const moveToward = (input: EnemyBehaviorInput, multiplier = 1): EnemyBehaviorResult => ({
  state: { kind: 'chase' },
  intent: { kind: 'move', dx: input.direction.x, dy: input.direction.y, speedMultiplier: multiplier },
});

export function advanceEnemyBehavior(input: EnemyBehaviorInput): EnemyBehaviorResult {
  if (input.state.kind === 'dead') return { state: input.state, intent: { kind: 'none' } };

  if (input.state.kind === 'recover') {
    if (input.nowMs < input.state.until) return { state: input.state, intent: { kind: 'none' } };
    return { state: { kind: 'chase' }, intent: { kind: 'none' } };
  }

  if (input.state.kind === 'telegraph') {
    if (input.nowMs < input.state.until) {
      return {
        state: input.state,
        intent: { kind: 'show-telegraph', attack: input.state.attack, until: input.state.until },
      };
    }
    const recoveryMs = Math.round(input.recoveryMs * timingMultiplier(input));
    const state: EnemyBehaviorState = { kind: 'recover', until: input.nowMs + recoveryMs };
    if (input.state.attack === 'lunge') {
      return { state, intent: { kind: 'lunge', dx: input.direction.x, dy: input.direction.y } };
    }
    if (input.state.attack === 'slam') return { state, intent: { kind: 'slam', radius: input.attackRange } };
    return { state, intent: { kind: 'shoot', dx: input.direction.x, dy: input.direction.y } };
  }

  if (input.state.kind === 'idle') {
    return input.distance < 200 ? { state: { kind: 'chase' }, intent: { kind: 'none' } } : { state: input.state, intent: { kind: 'none' } };
  }

  if (input.archetype === 'ranged' && input.distance < input.preferredRange * 0.65) {
    return {
      state: { kind: 'reposition' },
      intent: {
        kind: 'move',
        dx: -input.direction.x,
        dy: -input.direction.y,
        speedMultiplier: 1,
      },
    };
  }

  if (input.state.kind === 'reposition' && input.distance < input.preferredRange) {
    return {
      state: input.state,
      intent: {
        kind: 'move',
        dx: -input.direction.x,
        dy: -input.direction.y,
        speedMultiplier: 1,
      },
    };
  }

  if (input.distance <= input.attackRange) {
    const attack = attackFor(input.archetype);
    const until = input.nowMs + Math.round(input.telegraphMs * timingMultiplier(input));
    return {
      state: { kind: 'telegraph', attack, startedAt: input.nowMs, until },
      intent: { kind: 'show-telegraph', attack, until },
    };
  }

  return moveToward(input);
}
